/**
 * ProjectCreateModal - Modal for creating new projects
 * Multi-tenancy aware - requires customer selection
 */

import { useState } from 'react';
// X icon removed - using Dialog component close button instead
import { projectsApi, getApiErrorMessage } from '../services/api';
import type { ProjectInstance } from '../types';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  Button,
  Input,
  Select,
  Label,
  Spinner,
} from '@/components/ui';

interface ProjectCreateModalProps {
  onClose: () => void;
  onCreated: (newProject: ProjectInstance) => void;
  defaultCustomer?: string;
  open?: boolean;
}

export function ProjectCreateModal({ onClose, onCreated, defaultCustomer, open = true }: ProjectCreateModalProps) {
  const [name, setName] = useState('');
  const [projectCode, setProjectCode] = useState('');
  const [projectTitle, setProjectTitle] = useState('');
  const [customer, setCustomer] = useState(defaultCustomer || '');
  const [program, setProgram] = useState('');
  const [certAuthority, setCertAuthority] = useState('FAA');
  const [certBasis, setCertBasis] = useState<string[]>([]);
  const [projectType, setProjectType] = useState<'aerospace' | 'creative' | 'technical'>('aerospace');
  const [seriesName, setSeriesName] = useState('');
  const [codeCheckMessage, setCodeCheckMessage] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Check project code availability
  const handleProjectCodeChange = async (code: string) => {
    const upperCode = code.toUpperCase().replace(/[^A-Z0-9]/g, '');
    setProjectCode(upperCode);

    if (upperCode.length < 3) {
      setCodeCheckMessage('');
      return;
    }

    try {
      const isAvailable = await projectsApi.checkCodeUnique(upperCode);
      setCodeCheckMessage(isAvailable ? '✓ Code available' : '✗ Already in use');
    } catch {
      setCodeCheckMessage('');
    }
  };

  const handleCreate = async () => {
    if (!name.trim() || !customer.trim()) return;
    
    setSaving(true);
    setError(null);

    try {
      const newProject = await projectsApi.create(name.trim(), {}, {
        projectCode: projectCode.trim() || undefined,
        projectTitle: projectTitle.trim() || undefined,
        customer: customer.trim(),
        program: program.trim() || undefined,
        certificationAuthority: projectType === 'aerospace' ? certAuthority : undefined,
        certificationBasis: projectType === 'aerospace' && certBasis.length > 0 ? certBasis : undefined,
        projectType,
        seriesName: projectType === 'creative' && seriesName.trim() ? seriesName.trim() : undefined,
      });

      onCreated(newProject);
    } catch (err: unknown) {
      setError(getApiErrorMessage(err, 'Failed to create project'));
    } finally {
      setSaving(false);
    }
  };

  const toggleCertBasis = (basis: string) => {
    if (certBasis.includes(basis)) {
      setCertBasis(certBasis.filter(b => b !== basis));
    } else {
      setCertBasis([...certBasis, basis]);
    }
  };

  const isCodeInvalid = codeCheckMessage.includes('Already in use');

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
      <DialogContent className="max-w-[600px] max-h-[90vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle>New Project</DialogTitle>
        </DialogHeader>

        {/* Scrollable Body */}
        <div className="flex-1 overflow-y-auto space-y-4 py-2">
          {error && (
            <div className="p-3 bg-destructive-light border border-destructive rounded-lg text-destructive text-sm">
              {error}
            </div>
          )}

          {/* Project Name */}
          <div className="space-y-2">
            <Label htmlFor="project-name">Project Name *</Label>
            <Input
              id="project-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g., Navigation Display System"
              autoFocus
            />
            <p className="text-xs text-muted-foreground">
              Short name for internal reference
            </p>
          </div>

          {/* Customer (Required for multi-tenancy) */}
          <div className="space-y-2">
            <Label htmlFor="customer">Customer *</Label>
            <Input
              id="customer"
              value={customer}
              onChange={(e) => setCustomer(e.target.value)}
              placeholder="e.g., Boeing"
            />
            <p className="text-xs text-muted-foreground">
              Customer or organization for this project
            </p>
          </div>

          {/* Project Type */}
          <div className="space-y-2">
            <Label htmlFor="project-type">Project Type *</Label>
            <Select
              id="project-type"
              value={projectType}
              onChange={(e) => setProjectType(e.target.value as 'aerospace' | 'creative' | 'technical')}
            >
              <option value="aerospace">Aerospace / DO-178C / Certification</option>
              <option value="creative">Creative Writing / Fiction</option>
              <option value="technical">Technical Documentation</option>
            </Select>
            <p className="text-xs text-muted-foreground">
              {projectType === 'creative' 
                ? 'AI tools for character tracking, consistency, and creative workflows'
                : projectType === 'aerospace'
                ? 'AI tools for certification documents, traceability, and compliance'
                : 'AI tools for technical writing and documentation'}
            </p>
          </div>

          {/* Series Name (Creative projects only) */}
          {projectType === 'creative' && (
            <div className="space-y-2">
              <Label htmlFor="series-name">Series Name</Label>
              <Input
                id="series-name"
                value={seriesName}
                onChange={(e) => setSeriesName(e.target.value)}
                placeholder="e.g., Detective Marlow Mysteries"
              />
              <p className="text-xs text-muted-foreground">
                Leave blank for standalone novels. For series, all books share characters and continuity.
              </p>
            </div>
          )}

          {/* Project Code */}
          <div className="space-y-2">
            <Label htmlFor="project-code">Project Code</Label>
            <Input
              id="project-code"
              value={projectCode}
              onChange={(e) => handleProjectCodeChange(e.target.value)}
              placeholder="e.g., NAV-100"
              maxLength={10}
              className="uppercase"
            />
            {codeCheckMessage && (
              <p className={`text-xs font-medium ${
                codeCheckMessage.includes('✓') ? 'text-success' : 'text-destructive'
              }`}>
                {codeCheckMessage}
              </p>
            )}
            <p className="text-xs text-muted-foreground">
              Used in document numbers (optional but recommended)
            </p>
          </div>

          {/* Full Title */}
          <div className="space-y-2">
            <Label htmlFor="project-title">Full Project Title</Label>
            <Input
              id="project-title"
              value={projectTitle}
              onChange={(e) => setProjectTitle(e.target.value)}
              placeholder="e.g., Navigation Display System for Boeing 737"
            />
          </div>

          {/* Program */}
          <div className="space-y-2">
            <Label htmlFor="program">Program</Label>
            <Input
              id="program"
              value={program}
              onChange={(e) => setProgram(e.target.value)}
              placeholder="e.g., 737 MAX"
            />
          </div>

          {/* Certification Fields (Aerospace only) */}
          {projectType === 'aerospace' && (
            <>
              {/* Certification Authority */}
              <div className="space-y-2">
                <Label htmlFor="cert-authority">Certification Authority</Label>
                <Select
                  id="cert-authority"
                  value={certAuthority}
                  onChange={(e) => setCertAuthority(e.target.value)}
                >
                  <option value="FAA">FAA (Federal Aviation Administration)</option>
                  <option value="EASA">EASA (European Aviation Safety Agency)</option>
                  <option value="TCCA">TCCA (Transport Canada)</option>
                  <option value="Other">Other</option>
                </Select>
              </div>

              {/* Certification Basis */}
              <div className="space-y-2">
                <Label>Certification Basis</Label>
                <div className="space-y-2">
                  {[
                    { id: 'DO-178C', label: 'DO-178C (Software)' },
                    { id: 'DO-254', label: 'DO-254 (Hardware)' },
                    { id: 'ARP4754A', label: 'ARP4754A (Systems)' },
                    { id: 'ARP4761', label: 'ARP4761 (Safety)' },
                  ].map(({ id, label }) => (
                    // eslint-disable-next-line no-restricted-syntax -- Native label/checkbox for certification checkboxes
                    <label key={id} className="flex items-center gap-2 cursor-pointer">
                      {/* eslint-disable-next-line no-restricted-syntax -- Native checkbox for form */}
                      <input
                        type="checkbox"
                        checked={certBasis.includes(id)}
                        onChange={() => toggleCertBasis(id)}
                        className="w-4 h-4 rounded border-input text-primary focus:ring-primary"
                      />
                      <span className="text-sm">{label}</span>
                    </label>
                  ))}
                </div>
              </div>
            </>
          )}

          {/* Creative Project Info */}
          {projectType === 'creative' && (
            <div className="p-3 bg-muted/50 border border-border rounded-lg">
              <p className="text-sm text-muted-foreground">
                <strong>Creative Project Features:</strong>
              </p>
              <ul className="text-xs text-muted-foreground mt-2 space-y-1 list-disc list-inside">
                <li>Character tracking with consistency validation</li>
                <li>Timeline and plot continuity checking</li>
                <li>Evidence and clue registry for mysteries</li>
                <li>Series continuity across multiple books</li>
                <li>AI writing context injection</li>
              </ul>
            </div>
          )}
        </div>

        {/* Footer */}
        <DialogFooter>
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button
            onClick={handleCreate}
            disabled={!name.trim() || !customer.trim() || saving || isCodeInvalid}
          >
            {saving && <Spinner size="sm" className="mr-2" />}
            {saving ? 'Creating...' : 'Create Project'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
